import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { handleLogin } from '@blaze-cms/core-auth-ui';
import parseHTML from 'html-react-parser';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { LoadingComponent } from '@blaze-cms/react-page-builder';
import FormInput from '../FormInput';
import { CREATE_USER } from '../../services/mutations';
import { GET_COUNTIES_BY_COUNTRY_ID } from '../../services/querys';
import buildSelectOptions from '../helpers/build-select-options';
import { formatForRender } from '../helpers/format-for-render';
import { fillFormSchema } from './helpers';
import extractValidationSchema from '../helpers/extract-validation-schema';
import BlazeSecondName from '../../../../components/BlazeSecondName';

const RegistrationForm = ({
  modifier,
  children,
  initialSchema,
  countries,
  organizationTypes,
  subscriptionId,
  subscriptionTypeId,
  redirectUrl
}) => {
  const router = useRouter();
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [showSuccessMessage, setShowSuccesMessage] = useState(false);
  const [formSchema, setFormSchema] = useState(initialSchema);

  const validationSchema = extractValidationSchema(formSchema);
  const resolver = ajvResolver(validationSchema, { allErrors: true, strict: true, $data: true });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors, dirtyFields, touchedFields }
  } = useForm({
    resolver,
    mode: 'onBlur',
    reValidateMode: 'onChange'
  });

  const blazeSecondNameValue = watch('blazeSecondName');

  const { refetch } = useQuery(GET_COUNTIES_BY_COUNTRY_ID, {
    variables: { countryId: '' },
    skip: true
  });

  const watchedCountry = watch('country');
  const watchedPassword = watch('password');
  const prevCountryRef = useRef(watchedCountry);

  useEffect(() => {
    if (watchedPassword && watchedPassword.length > 0) {
      trigger('password');

      if (dirtyFields.passwordConfirm) {
        trigger('passwordConfirm');
      }
    }
  }, [watchedPassword, trigger, dirtyFields.passwordConfirm]);

  useEffect(() => {
    const updateCounties = async () => {
      if (!watchedCountry) return;
      const foundCountry = countries.find(c => c.id === watchedCountry);

      const { data: { counties: newCounties = [] } = {} } = await refetch({
        countryId: foundCountry.id
      });

      const countyOptions = buildSelectOptions(newCounties);
      const updatedSchema = fillFormSchema(initialSchema, countyOptions);

      if (prevCountryRef.current !== watchedCountry) {
        setValue('county', '', { shouldValidate: false });
        prevCountryRef.current = watchedCountry;
      }

      setFormSchema(updatedSchema);

      setTimeout(() => {
        if (errors.county) {
          trigger('county');
        }
      }, 0);
    };

    updateCounties();
  }, [
    countries,
    organizationTypes,
    initialSchema,
    watchedCountry,
    refetch,
    setValue,
    trigger,
    errors
  ]);

  const [createUser, { loading, client }] = useMutation(CREATE_USER, {
    onCompleted: async data => {
      setShowErrorMessage(false);
      setShowSuccesMessage(true);
      await handleLogin(data.registerUser, client);
      router.push('/Resolver', redirectUrl || '/');
    },
    onError: error => {
      setShowErrorMessage(error.message);
      setShowSuccesMessage(false);
    }
  });

  const handleRegister = async data => {
    const {
      email,
      password,
      firstname,
      lastname,
      title,
      jobTitle,
      organisation: company,
      organizationTypeId,
      telephoneNumber: tel,
      mobileNumber: mobile
    } = data;
    const user = {
      email,
      password,
      firstname,
      lastname,
      title,
      jobTitle,
      company,
      organizationTypeId,
      tel,
      mobile
    };

    const { line1: address1, line2: address2, city, zip: postcode, country, county } = data;

    const address = {
      address1,
      address2,
      city,
      postcode,
      country,
      county,
      tel
    };

    await createUser({
      variables: {
        input: {
          user,
          address,
          subscriptionId,
          subscriptionTypeId
        }
      }
    });
  };

  const handleSubmitForm = e => {
    if (touchedFields.blazeSecondName || blazeSecondNameValue?.trim()) {
      return;
    }
    handleRegister(e);
  };

  const parsedModifier = modifier ? ` ${modifier}` : '';
  const formFields = formatForRender(formSchema);

  return (
    <div className={`form-container${parsedModifier}`}>
      <form onSubmit={handleSubmit(handleSubmitForm)} className="form-container__form">
        {formSchema.sections.map((labell, i) => (
          <section key={labell}>
            <span className="form-container__sub_header">{labell}</span>
            {formFields
              .filter(groups => groups.section === i)
              .map((fieldGroup, _) => (
                <div className="form-container__input_group">
                  {Array(fieldGroup).map(fields =>
                    fields.fields.map(field => {
                      if (!field.showInForm) return null;
                      if (field.text)
                        return (
                          <p className="info" key={field.fieldId}>
                            {parseHTML(field.text)}
                          </p>
                        );
                      return (
                        <FormInput
                          key={field.fieldId}
                          id={field.fieldId}
                          label={field.label}
                          isRequired={field.isRequired}
                          formFieldType={field.formFieldType}
                          register={register}
                          placeholder={field.placeholder}
                          error={errors[field.fieldId]}
                          options={field.options}
                        />
                      );
                    })
                  )}
                </div>
              ))}
          </section>
        ))}
        <BlazeSecondName register={register} />
        <button
          className="button button--center form__submit-button"
          type="submit"
          disabled={loading}>
          {loading ? <LoadingComponent /> : 'Continue'}
        </button>
        {showErrorMessage && (
          <div className="form error_message_wrapper">
            <div className="error_message_icon" />
            <div className="error_message">{showErrorMessage}</div>
          </div>
        )}
      </form>
      {showSuccessMessage && children}
    </div>
  );
};

RegistrationForm.propTypes = {
  initialSchema: PropTypes.object.isRequired,
  modifier: PropTypes.string,
  children: PropTypes.any,
  countries: PropTypes.array,
  organizationTypes: PropTypes.array,
  subscriptionId: PropTypes.string,
  subscriptionTypeId: PropTypes.string,
  redirectUrl: PropTypes.string
};

RegistrationForm.defaultProps = {
  modifier: '',
  children: null,
  countries: [],
  organizationTypes: [],
  subscriptionId: '',
  subscriptionTypeId: '',
  redirectUrl: null
};

export default RegistrationForm;
